import { RestError } from '@azure/core-http';
import { useQueryGetProject } from '../../../../../core/queries/projects.queries';
import { useForm } from 'react-hook-form';
import { useImmer } from 'use-immer';
import { EditUserActionProps } from '../edit-user/edit-user-action.component';
import { useMutationEditCustomer, useMutationEditOccupant } from '../../../users.queries';
import { mapProjectTypeToUserType } from '../../../users.constants';
import {
  KnownProjectModuleSettingsDtoOccupantRules,
  KnownProjectModuleSettingsDtoStatus,
  KnownUserDtoUserType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { castDraft } from 'immer';
import { useQueryGetProjectModules } from '../../../../project-modules/queries';
import { convertHyphenated } from '../../../../../core/util/string-conversion';
import { deliveryTypeToIconMap } from '../../../../deliveries/maps/delivery-type-to-icon.map';
import { mapProjectModuleToExternalDeliveryType } from '../../../../project-modules/mappings';
import { useHasUIFeatures } from '../../../../../core/permissions/has-permissions.hook';
import { SVGIconComponent } from '../../../../../ui/components/pure/icons/types';

export type EditUserCapabilitiesState = {
  modules: string[];
};

export type EditUserActionState = {
  saving: boolean;
  loading: boolean;
  error?: RestError;
};

export type ProjectModuleAssignedToUser = {
  isDefaultSelected?: boolean;
  isSelected?: boolean;
  moduleStatus?: string;
  occupantRules?: string;
  label: string;
  Icon: SVGIconComponent;
  value: string;
};

export type ProjectModuleInheritedFromProject = {
  status: string;
  label: string;
  Icon: SVGIconComponent;
  value: string;
};

export interface EditUserActionHandlers {
  onCancel(): void;
  onSubmit(form: EditUserCapabilitiesState): void;
}

export function useEditUserCapabilitiesAction({
  userDto,
  projectID,
  childProjectID,
  onAbort,
  onSuccess
}: EditUserActionProps) {
  const hasInternalPermission = useHasUIFeatures(['internal-groundfloor']);

  const [state, setState] = useImmer<EditUserActionState>({ loading: false, saving: false });

  const form = useForm<EditUserCapabilitiesState>({
    defaultValues: {
      modules: userDto?.projectModules || []
    }
  });

  const projectModules = useQueryGetProjectModules(projectID);

  const activeModules = (projectModules.data || []).filter(
    (module) => module.data.status === KnownProjectModuleSettingsDtoStatus.Active
  );

  const projectModulesAllowingSelectUsers = activeModules.filter(
    (module) => module.data.occupantRules === KnownProjectModuleSettingsDtoOccupantRules.Select
  );

  const projectModulesForProject: ProjectModuleInheritedFromProject[] = activeModules
    .filter((module) => module.data.occupantRules === KnownProjectModuleSettingsDtoOccupantRules.All)
    .map((module) => {
      const deliveryType = mapProjectModuleToExternalDeliveryType(module.schema.name);
      if (!deliveryType) return null;
      return {
        status: module.data.status,
        label: convertHyphenated(module.schema.name, { capitalizeEachWord: true }),
        Icon: deliveryTypeToIconMap[deliveryType],
        value: module.schema.name
      };
    })
    .filter((module): module is ProjectModuleInheritedFromProject => module !== null);

  const projectModulesForUser: ProjectModuleAssignedToUser[] = (userDto?.projectModules || [])
    .map((module) => {
      const deliveryType = mapProjectModuleToExternalDeliveryType(module);
      if (!deliveryType) return null;
      const projectModuleAssignedToUser: ProjectModuleAssignedToUser = {
        isDefaultSelected: true,
        isSelected: form.watch('modules')?.includes(module),
        moduleStatus: (projectModules.data || []).find((m) => m.schema.name === module)?.data.status,
        occupantRules: (projectModules.data || []).find((m) => m.schema.name === module)?.data.occupantRules,
        label: convertHyphenated(module, { capitalizeEachWord: true }),
        Icon: deliveryTypeToIconMap[deliveryType],
        value: module
      };
      return projectModuleAssignedToUser;
    })
    .filter((module): module is ProjectModuleAssignedToUser => module !== null);

  const otherAvailableModules = projectModulesAllowingSelectUsers
    .filter((module) => !userDto?.projectModules.includes(module.schema.name))
    .map((module) => {
      const deliveryType = mapProjectModuleToExternalDeliveryType(module.schema.name);
      if (!deliveryType) return null;
      const projectModuleAssignedToUser: ProjectModuleAssignedToUser = {
        isDefaultSelected: false,
        isSelected: form.watch('modules')?.includes(module.schema.name),
        moduleStatus: module.data.status,
        occupantRules: module.data.occupantRules,
        label: convertHyphenated(module.schema.name, { capitalizeEachWord: true }),
        Icon: deliveryTypeToIconMap[deliveryType],
        value: module.schema.name
      };
      return projectModuleAssignedToUser;
    })
    .filter((module): module is ProjectModuleAssignedToUser => module !== null);

  projectModulesForUser.push(...otherAvailableModules);

  const visibleProjectModulesForUser = hasInternalPermission.hasAllFeatures
    ? projectModulesForUser
    : projectModulesForUser.filter((module) => module.moduleStatus === KnownProjectModuleSettingsDtoStatus.Active);

  const currentProject = useQueryGetProject(childProjectID ?? projectID);
  const projectType = currentProject.data?.projectType;
  const currentUserType = mapProjectTypeToUserType(projectType);

  const editCustomerMutation = useMutationEditCustomer(userDto?.id);
  const editOccupantMutation = useMutationEditOccupant(userDto?.id);

  function onChange(module: string, value: boolean) {
    setState((draft) => {
      draft.error = undefined;
    });

    form.setValue(
      'modules',
      (form.getValues('modules') || []).filter((m) => m !== module)
    );
    if (value) {
      form.setValue('modules', [...(form.getValues('modules') || []), module]);
    }
  }

  function onSubmit(form: EditUserCapabilitiesState) {
    if (!userDto || !currentUserType) {
      return;
    }
    setState((draft) => {
      draft.saving = true;
      draft.error = undefined;
    });

    const modules = form.modules || [];
    const projectIDToUse = childProjectID || projectID;

    if (currentUserType === KnownUserDtoUserType.Customer) {
      editCustomerMutation.mutate(
        {
          retailerID: projectIDToUse,
          editCustomerDto: {
            projectModules: modules
          }
        },
        {
          onSuccess() {
            onSuccess();
          },
          onError(error) {
            setState((draft) => {
              draft.saving = false;
              draft.error = castDraft(error);
            });
          }
        }
      );
    }

    if (currentUserType === KnownUserDtoUserType.Occupant) {
      editOccupantMutation.mutate(
        {
          projectModules: modules
        },
        {
          onSuccess() {
            onSuccess();
          },
          onError(error) {
            setState((draft) => {
              draft.saving = false;
              draft.error = castDraft(error);
            });
          }
        }
      );
    }
  }

  return {
    form,
    state,
    handlers: {
      onCancel: onAbort,
      onSubmit,
      onChange
    },
    projectModulesForProject,
    projectModulesForUser: visibleProjectModulesForUser,
    isLoading: currentProject.isLoading || editCustomerMutation.isLoading || editOccupantMutation.isLoading
  };
}
